import React, { useState } from "react";
import { FileText, Flame, MessagesSquare, Sparkles, Sun } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useLoading } from "@/lib/loading";
import { useToast } from "@/lib/toast";
import { Button, Card, Input, Overline } from "@/components/app/Primitives";

const MODES = [
  { key: "describe", label: "Describe task", icon: FileText, hint: "Turn a title into a clear task brief." },
  { key: "priority", label: "Suggest priority", icon: Flame, hint: "Get an AI-suggested priority level." },
  { key: "summarize", label: "Summarize", icon: MessagesSquare, hint: "Condense meeting notes or comments." },
  { key: "daily", label: "Daily report", icon: Sun, hint: "Generate today's report from your tasks." },
];
const MSG = { describe: "Writing a clear task description…", priority: "Analyzing priority level…", summarize: "Summarizing…", daily: "Building your daily report…" };

export default function AI() {
  const { activeWorkspace } = useAuth();
  const toast = useToast();
  const { showLoading, hideLoading } = useLoading();
  const [mode, setMode] = useState("describe");
  const [input, setInput] = useState("");
  const [output, setOutput] = useState("");
  const [loading, setLoading] = useState(false);

  const run = async () => {
    if (mode !== "daily" && !input.trim()) {
      return toast.show(mode === "describe" ? "Enter a task title" : mode === "priority" ? "Enter a task title or description" : "Paste text to summarize", "error");
    }
    if (mode === "daily" && !activeWorkspace) return toast.show("No workspace selected", "error");
    setLoading(true); setOutput(""); showLoading(MSG[mode]);
    try {
      if (mode === "describe") setOutput((await api("/ai/task-description", { method: "POST", body: { title: input.trim() } })).description);
      else if (mode === "priority") setOutput(`Suggested priority: ${(await api("/ai/priority-suggest", { method: "POST", body: { title: input.trim() } })).priority.toUpperCase()}`);
      else if (mode === "summarize") setOutput((await api("/ai/summarize", { method: "POST", body: { text: input.trim() } })).summary);
      else setOutput((await api("/ai/daily-report", { method: "POST", body: { workspace_id: activeWorkspace.id } })).report);
    } catch (e) { toast.show(e instanceof Error ? e.message : "AI request failed", "error"); }
    finally { setLoading(false); hideLoading(); }
  };

  return (
    <div className="page-in space-y-3.5 px-4 pt-4 md:pt-8" data-testid="ai-page">
      <div className="py-2">
        <Overline>AI Assistant</Overline>
        <h1 className="text-[28px] font-extrabold tracking-tight text-a-text">Work smarter</h1>
        <p className="mt-1 text-a-muted">Claude Sonnet 4.5 · powered by Assignoo</p>
      </div>
      <div className="ai-hero rounded-2xl border border-a-border p-4">
        <div className="flex flex-wrap gap-2.5">
          {MODES.map((m) => {
            const active = mode === m.key;
            return (
              <button key={m.key} data-testid={`ai-mode-${m.key}`} onClick={() => { setMode(m.key); setOutput(""); }}
                className={`flex items-center gap-1.5 rounded-xl border px-3 py-2.5 text-[13px] font-bold transition-colors ${active ? "border-a-primary bg-a-primary text-white" : "border-a-border bg-a-surface text-a-text hover:border-a-primary/50"}`}>
                <m.icon size={16} className={active ? "text-white" : "text-a-primary"} /> {m.label}
              </button>
            );
          })}
        </div>
        <p className="mt-3 text-[13px] text-a-muted">{MODES.find((m) => m.key === mode)?.hint}</p>
      </div>
      {mode !== "daily" ? (
        <Card>
          <Input testID="ai-input" label={mode === "summarize" ? "Text to summarize" : "Task title / prompt"}
            placeholder={mode === "summarize" ? "Paste meeting notes or comments…" : "e.g. Prepare monthly sales pitch deck"}
            value={input} onChangeText={setInput} multiline={mode === "summarize"} rows={5} onSubmitEditing={run} />
        </Card>
      ) : null}
      <Button testID="ai-generate-button" label={loading ? "Thinking…" : "Generate"} onPress={run} loading={loading} icon={<Sparkles size={16} />} />
      {output ? (
        <Card testID="ai-result">
          <div className="space-y-2">
            <div className="flex items-center gap-1.5 text-a-primary"><Sparkles size={14} /><Overline>Result</Overline></div>
            <p className="whitespace-pre-wrap text-[15px] leading-[22px] text-a-text">{output}</p>
          </div>
        </Card>
      ) : null}
    </div>
  );
}
